// Rewrites local references inside `@fun` `${...}` expressions before token parsing.
// Keeps bracket access support scoped to function-local expression expansion.
import { readVarCallToken } from '../../loops/for-loop/placeholder-expression-var'
import { parseExpressionLocalReferenceAt } from './parse-reference'
import type { ParsedLocalReference } from './parse-reference-types'

// Rewrite raw expression text by resolving prefixless local references.
export function rewriteExpressionLocalReferences(
  expression: string,
  resolveReference: (reference: ParsedLocalReference) => string | null,
): { value: string; changed: boolean } {
  let value = ''
  let changed = false

  // Scan expression source and keep string literals plus var-calls intact.
  for (let index = 0; index < expression.length;) {
    const stringLiteral = readStringLiteral(expression, index)
    if (stringLiteral) {
      value += stringLiteral.value
      index = stringLiteral.end
      continue
    }

    const varCall = readVarCallToken(expression, index)
    if (varCall) {
      value += varCall.value
      index = varCall.end
      continue
    }

    const reference = isReferenceBoundary(expression, index)
      ? parseExpressionLocalReferenceAt(expression, index)
      : null
    if (reference) {
      const resolved = resolveReference(reference)
      // Check whether the parsed local reference resolved successfully.
      if (resolved !== null) {
        value += resolved
        changed = true
        index = reference.end
        continue
      }
    }

    value += expression[index]
    index += 1
  }

  // Return the rewritten expression and change flag.
  return { value, changed }
}

// Read one quoted expression literal while preserving escape sequences.
function readStringLiteral(source: string, startIndex: number): { value: string; end: number } | null {
  const quote = source[startIndex]
  if (quote !== "'" && quote !== '"') return null

  for (let index = startIndex + 1; index < source.length; index += 1) {
    const char = source[index]
    const previous = source[index - 1] ?? ''
    // Check whether the quoted literal closes at the current position.
    if (char === quote && previous !== '\\') {
      // Return the raw quoted literal slice.
      return { value: source.slice(startIndex, index + 1), end: index + 1 }
    }
  }

  // Return null for unterminated expression string literals.
  return null
}

// Check whether the current index can start a standalone expression reference.
function isReferenceBoundary(source: string, startIndex: number): boolean {
  const char = source[startIndex] ?? ''
  const previous = startIndex === 0 ? '' : source[startIndex - 1]
  return isIdentifierStart(char) && !isIdentifierBody(previous)
}

// Check whether a char can start an identifier token.
function isIdentifierStart(char: string): boolean {
  return (char >= 'a' && char <= 'z') || (char >= 'A' && char <= 'Z') || char === '_'
}

// Check whether a char can continue an identifier token.
function isIdentifierBody(char: string): boolean {
  return isIdentifierStart(char) || (char >= '0' && char <= '9')
}
